import React from 'react';
import {
  CellInfo,
  RowInfo,
  ColumnProjection,
  GetColumnsProps,
} from './ticketListConfigurations.types';
import {
  voidedAccessor,
  ticketNumberAccessor,
  ticketedTimestampAccessor,
  truckAliasAccessor,
  driverNameAccessor,
  customerAccessor,
  addressAccessor,
  loadingPlantAccessor,
  mixDesignProductNumberAccessor,
  haulerAccessor,
  productLineAccessor,
} from './ticketColumnAccessors';
import { receivedColumn } from '../../../geotrax/incomingMaterials/MaterialReceivedCell';
import { hasIncomingTicketArrived } from '../../../../util/statusesUtil';

// DEV-4154: every incoming Ticket # cell reserves indicator space, mirroring
// the messages panel rows (MessageList's dot/acknowledgedDot pair) — filled
// red dot when the truck has arrived (OnJob or beyond), empty circle
// otherwise, so numbers never shift when a status lands. Styled globally in
// TicketList.module.scss.
const ticketNumberCell = (cellInfo: CellInfo) => {
  const arrived = !!cellInfo.original && hasIncomingTicketArrived(cellInfo.original);
  const indicator = arrived ? 'arrived-dot' : 'arrived-dot-empty';
  return (
    <div className="ticket-number-cell">
      <span className={indicator} data-test={indicator} />
      {cellInfo.value}
    </div>
  );
};

export default function getColumns(props: GetColumnsProps): ColumnProjection[] {
  const { cellStyle, columnWidths, headerStyle } = props;

  const driverNameCell = (cellInfo: CellInfo) => cellInfo.value;

  const tsluAccessor = (row: RowInfo) =>
    voidedAccessor(row?.voided, row ? props.getSecondsSinceLastUpdate!(row, props.positions) : 0);

  const tsluCell = (cellInfo: CellInfo) =>
    cellInfo?.value ? props.renderTimeSinceLastUpdate!(Number(cellInfo.value)) : '';

  const widthConfigurationsMap: { [key: string]: number } = columnWidths.reduce(
    (accumulator, currentValue) => ({
      ...accumulator,
      [currentValue.id]: currentValue.value,
    }),
    {},
  );

  const tickets = [
    {
      Header: 'Ticket #',
      id: 'ticketNumber',
      accessor: ticketNumberAccessor,
      Cell: ticketNumberCell,
      style: cellStyle,
      width: widthConfigurationsMap.ticketNumCurrent || 110,
      headerStyle,
    },
    {
      Header: 'Date',
      id: 'ticketedTimestamp',
      accessor: ticketedTimestampAccessor,
      style: cellStyle,
      width: widthConfigurationsMap.date || 120,
      headerStyle,
    },
    // DEV-5225: Hauler — a truck may be sub-hauling for another hauler.
    {
      Header: 'Hauler',
      id: 'hauler',
      accessor: haulerAccessor,
      className: 'name-cell',
      headerClassName: 'name-header',
      style: cellStyle,
      width: 160,
      headerStyle,
    },
    {
      Header: 'Truck',
      id: 'truckAlias',
      accessor: truckAliasAccessor,
      className: 'vehicle-cell',
      headerClassName: 'vehicle-header',
      width: widthConfigurationsMap.truckAliasCurrent || 145,
      style: cellStyle,
      headerStyle,
    },
    {
      Header: 'Current/Last Driver',
      id: 'driverName',
      accessor: driverNameAccessor,
      Cell: driverNameCell,
      className: 'driver-cell',
      headerClassName: 'driver-header',
      width: widthConfigurationsMap.driverFullName || 160,
      style: cellStyle,
      headerStyle,
    },
    {
      Header: 'Last Tablet Update',
      id: 'tslu',
      accessor: tsluAccessor,
      Cell: tsluCell,
      className: 'time-cell',
      headerClassName: 'time-header',
      style: cellStyle,
      width: widthConfigurationsMap.tslu || 195,
      headerStyle,
    },
    {
      Header: 'Customer',
      id: 'customerName',
      accessor: customerAccessor,
      className: 'name-cell',
      headerClassName: 'name-header',
      style: cellStyle,
      width: widthConfigurationsMap.customerNameCurrent || 200,
      headerStyle,
    },
    {
      Header: 'Delivery Address',
      id: 'address',
      accessor: addressAccessor,
      className: 'address-cell',
      headerClassName: 'address-header',
      minWidth: 200,
      width: widthConfigurationsMap.addressCurrent || 250,
      style: cellStyle,
      headerStyle,
    },
    // DEV-5225: Product Line of the material source, so Aggregates and Cement
    // tickets are told apart. The id is the tickets API sort key.
    {
      Header: 'Product Line',
      id: 'externalInfo.plantProductLine',
      accessor: productLineAccessor,
      className: 'name-cell',
      headerClassName: 'name-header',
      style: cellStyle,
      width: 130,
      headerStyle,
    },
    {
      Header: 'Material Source',
      id: 'loadingPlant',
      accessor: loadingPlantAccessor,
      className: 'ticket-cell',
      headerClassName: 'ticket-header',
      style: cellStyle,
      width: widthConfigurationsMap.ticketingPlantCurrent || 200,
      headerStyle,
    },
    {
      Header: 'Product Number',
      id: 'product',
      accessor: mixDesignProductNumberAccessor,
      className: 'name-cell',
      headerClassName: 'name-header',
      style: cellStyle,
      width: 200,
      headerStyle,
    },
    // DEV-4153: the acknowledge-material surface — the cell owns the whole
    // flow (checkbox gating, confirm modal, BE call, toast).
    receivedColumn({ width: 120, cellStyle, headerStyle, hasEditAccess: !!props.hasEditAccess }),
  ];

  return tickets;
}
